import React, { FunctionComponent, useState, useEffect, useRef } from 'react';
import grey from '@material-ui/core/colors/grey';
import { makeStyles, Button, Box, Grid, Theme, InputLabel } from '@material-ui/core';
import moment from 'moment';
import { Form as FormEngine, FormDefinition } from '@mylicenseone/forms';
import indefinite from 'indefinite';
import {
    Appointment,
    InspectionType,
    License,
    LoggedInUser,
    User,
    Form,
    DropDownField,
    Inspection,
    ViolationList,
    Violation,
    Application,
} from 'src/store/modules/application/types';
import Loader from 'src/components/Loader';
import { ErrorState } from 'src/components/FormDisplay/types';
import { extractFormData, restoreRecord } from 'src/components/FormDisplay/FormDisplay';
import FormFieldDisplay from 'src/components/FormDisplay/FormFieldDisplay';
import AutoCompleteSelect from 'src/components/AutoCompleteSelect';
import Modal from 'src/components/Modal';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { getRequiredScopes, useAuthenticationContext } from 'src/authentication';
import { Api } from 'src/store/services/services';
import { siteActions } from 'src/store/actions';

type AddInspectionDialogProps = {
    setIsAddInspectionModalOpen: Function;
    addInspection: Function;
    application?: License;
    inspectors: any;
    licenses?: License[];
    inspectionTypesList: InspectionType[];
    getInspectionTypeList: Function;
    appointment?: Appointment;
    loggedInUser: LoggedInUser;
    getAddInspectionFormById: Function;
    addInspectionForm: Form;
    loading: boolean;
    getOpenedViolations: Function;
    openedViolations: ViolationList;
    closeBulkViolations: Function;
};

const AddInspectionDialog: FunctionComponent<AddInspectionDialogProps> = (props) => {
    const useStyles = makeStyles((theme: Theme) => ({
        addInspectionDialog: {
            display: 'flex',
            flexGrow: 1,
        },
        addInspectionDialogLabel: {
            fontSize: 14,
            fontWeight: 500,
            color: theme.palette.text.secondary,
            textAlign: 'end',
            justifyContent: 'flex-end',
            width: '100%',
            lineHeight: '21px',
            paddingRight: '20px',
            display: 'flex',
        },
        addInspectionDialogCommentLabel: {
            fontSize: 14,
            fontWeight: 500,
            color: theme.palette.text.secondary,
            textAlign: 'end',
            justifyContent: 'flex-end',
            width: '100%',
            lineHeight: '21px',
            paddingRight: '20px',
            display: 'flex',
            paddingTop: '17px',
        },
        adddInspectionDialogValue: {
            fontSize: 14,
            fontWeight: 450,
            color: grey[600],
            width: '100%',
            lineHeight: '21px',
        },
        addInspectionDialogMain: {
            width: '100%',
        },
        margin: {
            margin: theme.spacing(1),
        },
        activationModal: {
            padding: 10,
        },
        modalHeader: {
            color: theme.palette.text.secondary,
            fontSize: 20,
            fontWeight: 500,
            marginBottom: 20,
        },
        modalBtn: {
            marginLeft: 20,
        },
        btnContainer: {
            marginTop: 25,
        },
        datePickerContainer: {
            backgroundColor: 'white',
            border: '1px solid #ced4da',
            borderRadius: 5,
            padding: '3px 0px 3px 10px',
            margin: '0px 0px',
        },
        datePicker: {
            margin: 0,
        },
        underline: {
            borderBottomColor: 'white',
            '&:before': {
                borderBottomColor: 'white',
            },
            '&:after': {
                borderBottomColor: 'white !important',
            },
            '&:hover:before': {
                borderBottomColor: ['white', '!important'],
            },
        },
        MuiOutlinedInput: {
            backgroundColor: 'white',
            border: '1px solid #ced4da',
            borderRadius: 5,
            '&:focus': {
                backgroundColor: 'white',
                border: '1px solid #ced4da',
            },
            '&:hover': {
                backgroundColor: ['white', '!important'],
                border: '1px solid #ced4da',
            },
        },
    }));

    const {
        setIsAddInspectionModalOpen,
        addInspection,
        application,
        inspectors,
        inspectionTypesList,
        licenses,
        getInspectionTypeList,
        appointment,
        loggedInUser,
        getAddInspectionFormById,
        addInspectionForm,
        loading,
        getOpenedViolations,
        openedViolations,
        closeBulkViolations,
    } = props;

    const classes = useStyles();
    const [isConfirmationModalOpen, setModal] = useState(false);
    const [selectedInspector, setSelectedInspector] = useState<License>();
    const [inspectionType, setInspectionType] = useState<InspectionType>();

    const handleChangeInspectionType = (data: DropDownField | null) => {
        const inspectionType = inspectionTypesList!.find((item: InspectionType) => item.id === data?.value);
        setInspectionType(inspectionType);
    };

    const [violations, setViolations] = useState<DropDownField[] | null>();
    const handleChangeViolations = (data: DropDownField[] | null) => {
        setViolations(data);
    };

    const [license, setLicense] = useState<License>();
    const formId = inspectionType?.views?.add?.formId;
    const defaultInspector = inspectors.find((inspector: User) => {
        return inspector.id === loggedInUser.accountIdentifier || undefined;
    });
    const [formData, setFormData] = useState<Record<string, any>>({});
    const [inspectionInfo, setInspectionInfo] = useState<Inspection>();
    const [formEngine, setFormEngine] = useState<FormEngine>();
    const [formErrors, setFormErrors] = useState<Record<string, ErrorState>>({});
    const formErrorsRef = useRef<Record<string, ErrorState>>({});
    useEffect(() => {
        getOpenedViolations(application?.id ?? license?.id);
    }, [application, getOpenedViolations, license]);
    useEffect(() => {
        if (addInspectionForm?.id) {
            const inspection: Inspection = {
                id: '',
                type: {
                    id: '',
                    name: '',
                },
                inspector: { id: defaultInspector?.id, name: defaultInspector?.name } || undefined,
                license: {
                    id: license?.id || '',
                    licenseNumber: license?.licenseNumber || '',
                    type: {
                        id: license?.type?.id || '',
                        name: license?.type?.name || '',
                    },
                },
                inspectionDate: appointment?.startDateTime
                    ? moment(appointment!.startDateTime).format('YYYY-MM-DD')
                    : undefined,
                status: '',
            };

            let inspectionObject = extractFormData(inspection, addInspectionForm);
            inspectionObject = { ...inspectionObject, ...{ _context: { license } } };
            setFormData(inspectionObject);
        }
    }, [addInspectionForm, appointment, defaultInspector, license]);

    useEffect(() => {
        getInspectionTypeList({
            licenseTypeId: license?.type?.id,
        });
    }, [getInspectionTypeList, license]);

    useEffect(() => {
        formEngine?.evaluateApplicationData(formData, { validateDefinedOnly: true });
    }, [formEngine, formData]);

    useEffect(() => {
        if (addInspectionForm.sections) {
            const engine = new FormEngine(addInspectionForm as FormDefinition);

            engine.onValidationChange((field, error, messages) => {
                formErrorsRef.current = {
                    ...formErrorsRef.current,
                    [field]: {
                        error,
                        messages,
                    },
                };

                setFormErrors(formErrorsRef.current);
            });

            setFormEngine(engine);
        } else {
            setFormEngine(undefined);
        }
        setFormErrors({});
    }, [addInspectionForm]);

    useEffect(() => {
        setFormErrors({});
        getAddInspectionFormById(formId);
    }, [formId, getAddInspectionFormById]);

    const handleInputChange = (dataKey: any, value: string) => {
        setFormData({
            ...formData,
            [dataKey]: value,
        });
    };

    const authContext = useAuthenticationContext();
    const { scopes } = authContext;
    const getAccessToken = authContext.getAccessToken;

    const addInspectorInspection = (addInspectionInfo = undefined) => {
        addInspection(inspectionInfo ? inspectionInfo : addInspectionInfo);
        if (violations?.length && (application?.id ?? license?.id)) {
            const selectedViolations = violations.map((item) => item.value);
            closeBulkViolations(selectedViolations, application?.id ?? license?.id);
        }
        setIsAddInspectionModalOpen(false);
    };
    const handleAddInspection = async () => {
        await formEngine?.evaluateApplicationData(formData);

        if (!formEngine?.applicationHasErrors()) {
            const inspectionRecord = restoreRecord({}, formData, addInspectionForm);
            const addInspectionInfo = {
                ...inspectionRecord,
                type: {
                    id: inspectionType?.id,
                    name: inspectionType?.name,
                },
                license: {
                    id: license?.id,
                    licenseNumber: license?.licenseNumber,
                    type: {
                        id: license?.type?.id,
                        name: license?.type?.name,
                    },
                },
            };

            if (inspectionRecord?.inspector?.id) {
                addInspectionInfo.inspector = inspectionRecord.inspector.id.includes('search->')
                    ? { license: { id: inspectionRecord?.inspector.id.replace('search->', '') } }
                    : { id: inspectionRecord?.inspector.id };
            } else {
                addInspectionInfo.inspector = undefined;
            }

            if (appointment) {
                addInspectionInfo.event = {
                    id: appointment.id,
                    type: {
                        id: appointment.type?.id,
                        name: appointment.type?.name,
                    },
                };
            }
            //If the selected inspector is 3rd party inspector, then check the category configurations
            if (inspectionRecord.inspector.id.includes('search->')) {
                let url: string = '/licenseTypes';

                const scope = getRequiredScopes(url);
                const workflowToken = await getAccessToken({ scopes: [scopes['workflows.read']] }).then(
                    (response) => response.accessToken,
                );
                if (scope && workflowToken) {
                    try {
                        const token = await authContext
                            .getAccessToken({ scopes: [scopes[scope]] })
                            .then((response) => response.accessToken);
                        const licenseType = await Api.getLicenseType(token, selectedInspector?.type?.id);
                        const workflowDetails = await Api.getWorkflowById(workflowToken, licenseType.workflowId);
                        //Check for category status configured for 3rd party inspector
                        const isCategoryFieldActive: boolean = workflowDetails.states.some((workFlowState) => {
                            if (workFlowState.category) {
                                if (
                                    selectedInspector?.status?.toLowerCase() === workFlowState.category.toLowerCase() &&
                                    workFlowState.category.toLowerCase() === 'active'
                                ) {
                                    return true;
                                }
                            }
                            return false;
                        });
                        setInspectionInfo(addInspectionInfo);
                        //If category field is not configured or status is 'active'
                        //check the selected inspector status
                        if (!isCategoryFieldActive) {
                            //If the status is other than active show a warning modal
                            if (selectedInspector?.status.toLowerCase() !== 'active') {
                                setModal(true);
                            } else {
                                addInspectorInspection(addInspectionInfo);
                            }
                        } else {
                            addInspectorInspection(addInspectionInfo);
                        }
                    } catch (err) {
                        siteActions.setError(err);
                    }
                }
            } else {
                addInspectorInspection(addInspectionInfo);
            }
        }
    };

    useEffect(() => {
        if (appointment && appointment.inspectionType) {
            const defaultInspectionType = inspectionTypesList.find((inspectionType: InspectionType) => {
                return inspectionType.id === appointment.inspectionType?.id;
            });
            setInspectionType(defaultInspectionType);
        }
    }, [appointment, inspectionTypesList]);

    useEffect(() => {
        setLicense(application);
    }, [application]);

    const handleChangeLicense = (data: DropDownField | null) => {
        const license = licenses!.find((item) => item.id === data?.value);
        setLicense(license);
    };

    const checkDisable = () => {
        return (
            !inspectionType ||
            (!!addInspectionForm && !addInspectionForm.sections) ||
            Object.values(formErrors).some((error) => error.error)
        );
    };

    const handleSelectedInspector = (selectedInspectorDetails: License) => {
        setSelectedInspector(selectedInspectorDetails);
    };

    const onClose = () => {
        setModal(false);
    };
    const onConfirm = () => {
        setModal(false);
        addInspectorInspection();
        setIsAddInspectionModalOpen(false);
    };

    const LicenseSelect: FunctionComponent = () => {
        return (
            <Grid container style={{ marginTop: '10px' }}>
                <Grid item xs={3} sm={3} md={3} lg={3} style={{ display: 'flex', alignItems: 'center' }}>
                    <Box component="div" className={classes.addInspectionDialogLabel}>
                        Select License
                    </Box>
                </Grid>
                <Grid item xs={9} sm={9} md={9} lg={9}>
                    <Box className={classes.adddInspectionDialogValue}>
                        <InputLabel htmlFor="issued-by-select-native" style={{ display: 'none' }}>
                            Select License
                        </InputLabel>
                        <AutoCompleteSelect
                            data={
                                Array.isArray(licenses)
                                    ? licenses?.map((license: Application) => ({
                                          label:
                                              license.licenseNumber + ' - ' + (license.additionalData?.rideName ?? ''),
                                          value: license.id,
                                      }))
                                    : []
                            }
                            isMultipleValue={false}
                            onChange={handleChangeLicense}
                            inputSize={'small'}
                            value={
                                license?.id
                                    ? {
                                          label: license.licenseNumber,
                                          value: license.id,
                                      }
                                    : null
                            }
                            variant={'outlined'}
                        />
                    </Box>
                </Grid>
            </Grid>
        );
    };

    const content = (
        <div>
            You have selected an inspector with{' '}
            <strong>{indefinite(!!selectedInspector ? selectedInspector?.status?.toLowerCase() : '')}</strong>{' '}
            <strong>{selectedInspector?.type.name}</strong>.
        </div>
    );
    if (!inspectionTypesList.length && loading) return <Loader />;
    return (
        <Box className={classes.addInspectionDialog}>
            <Grid container className={classes.activationModal}>
                <div className={classes.modalHeader}>Add Inspection</div>
                <Box component="div" className={classes.addInspectionDialogMain}>
                    {appointment && licenses && <LicenseSelect />}

                    {openedViolations?.length ? (
                        <Grid container style={{ marginTop: '10px' }}>
                            <Grid item xs={3} sm={3} md={3} lg={3} style={{ display: 'flex', alignItems: 'center' }}>
                                <Box component="div" className={classes.addInspectionDialogLabel}>
                                    Open Violations
                                </Box>
                            </Grid>
                            <Grid item xs={9} sm={9} md={9} lg={9}>
                                <Box className={classes.adddInspectionDialogValue}>
                                    <InputLabel htmlFor="inspection-type-select-native" style={{ display: 'none' }}>
                                        Open Violations
                                    </InputLabel>
                                    <AutoCompleteSelect
                                        isMultipleValue={true}
                                        placeholder="Select violations to close"
                                        id="inspection-type-select"
                                        data={
                                            Array.isArray(openedViolations)
                                                ? openedViolations?.map((violation: Violation) => ({
                                                      label: `${violation?.inspection?.type?.name ?? ''} ${
                                                          moment(violation?.inspection?.inspectionDate).format('L') ??
                                                          ''
                                                      } ${violation?.category?.name}`,
                                                      value: violation?.id,
                                                  }))
                                                : []
                                        }
                                        onChange={handleChangeViolations}
                                        inputSize={'small'}
                                        value={violations}
                                        variant={'outlined'}
                                        ariaLabel="Violations"
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    ) : null}
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid item xs={3} sm={3} md={3} lg={3} style={{ display: 'flex', alignItems: 'center' }}>
                            <Box component="div" className={classes.addInspectionDialogLabel}>
                                Inspection Type
                            </Box>
                        </Grid>
                        <Grid item xs={9} sm={9} md={9} lg={9}>
                            <Box className={classes.adddInspectionDialogValue}>
                                <InputLabel htmlFor="inspection-type-select-native" style={{ display: 'none' }}>
                                    Inspection Type
                                </InputLabel>
                                <AutoCompleteSelect
                                    id="inspection-type-select"
                                    data={
                                        Array.isArray(inspectionTypesList)
                                            ? inspectionTypesList?.map((type: InspectionType) => ({
                                                  label: type.name,
                                                  value: type.id,
                                              }))
                                            : []
                                    }
                                    onChange={handleChangeInspectionType}
                                    inputSize={'small'}
                                    value={
                                        inspectionType?.id
                                            ? {
                                                  label: inspectionType.name,
                                                  value: inspectionType.id,
                                              }
                                            : null
                                    }
                                    variant={'outlined'}
                                    ariaLabel="Inspection Type"
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    {!!addInspectionForm?.id &&
                        inspectionType &&
                        addInspectionForm?.sections.map((section) =>
                            section.entries.map((entry) => (
                                <Grid key={entry.field} container style={{ marginTop: '10px', width: '100%' }}>
                                    <Grid
                                        key={entry.field}
                                        item
                                        style={{ display: 'flex', alignItems: 'center', width: '100%' }}
                                    >
                                        <FormFieldDisplay
                                            readOnly={false}
                                            formField={entry}
                                            isCustomStyle={true}
                                            value={formData[entry.field]}
                                            error={formErrors[entry.field]}
                                            handleInputChange={handleInputChange}
                                            handleSelectedInspector={handleSelectedInspector}
                                            formData={formData}
                                        />
                                    </Grid>
                                </Grid>
                            )),
                        )}
                </Box>
                <Grid className={classes.btnContainer} sm={12} md={12} lg={12} item>
                    <Box display="flex" justifyContent="flex-end">
                        <Button
                            onClick={() => {
                                setIsAddInspectionModalOpen(false);
                            }}
                            className={classes.modalBtn}
                        >
                            CANCEL
                        </Button>
                        <Button
                            className={classes.modalBtn}
                            onClick={handleAddInspection}
                            variant="contained"
                            color="primary"
                            disabled={checkDisable()}
                        >
                            ADD
                        </Button>
                    </Box>
                </Grid>
                <Modal open={isConfirmationModalOpen} maxWidth={'sm'} fullWidth={false} minHeight={'10vh'}>
                    <ConfirmationDialog
                        onClose={onClose}
                        onDismiss={onConfirm}
                        buttonContent="ADD"
                        cancelButtonContent="BACK"
                        contentTitle="Add Inspection?"
                        content={content}
                    />
                </Modal>
            </Grid>
        </Box>
    );
};

export default AddInspectionDialog;
